/**
 * Wire format types for the MCP Garden endpoints exposed by the AI Ecosystem API.
 * Field names mirror the backend contract 1:1 and are consumed directly by the UI.
 */

import {
  AccessRequestStatus,
  AccessReviewStatus,
  CloudDeployment,
  ContactData,
  OnboardingStatus,
} from './global.objects';
import {
  AccessRequestBase,
  AccessRequestBulkUpdateResponseBase,
  AccessRequestBulkUpdateResultBase,
} from './access-management.objects';

// ---------------------------------------------------------------------------------------------------- //
// CLASSES
// ---------------------------------------------------------------------------------------------------- //

/**
 * Error thrown when a requested MCP server cannot be found.
 */
export class McpServerNotFoundError extends Error {
  constructor(mcpServerId: string) {
    super(`MCP server with id '${mcpServerId}' not found`);
    this.name = 'MCPServerNotFoundError';
  }
}

// ---------------------------------------------------------------------------------------------------- //
// CONSTANTS
// ---------------------------------------------------------------------------------------------------- //

/** Server types supported by the MCP Garden. */
export const MCP_SERVER_TYPES = ['Internal', 'Public'] as const;

/** Hosting variants supported by the MCP Garden. */
export const MCP_SERVER_DEPLOYMENT_MODELS = ['Local', 'Remote'] as const;

/** Authentication mechanisms supported for remote MCP servers. */
export const MCP_AUTH_TYPES = ['API Key', 'OAuth Client Credentials'] as const;

/** Configuration types supported by the MCP Garden. */
export const MCP_CONFIGURATION_TYPES = [
  'Visual Studio Code',
  'Claude',
  'MCP Server Config (server.json)',
] as const;

// ---------------------------------------------------------------------------------------------------- //
// TYPES
// ---------------------------------------------------------------------------------------------------- //

export type McpServerType = (typeof MCP_SERVER_TYPES)[number];
export type McpServerDeploymentModel =
  (typeof MCP_SERVER_DEPLOYMENT_MODELS)[number];
export type McpAuthType = (typeof MCP_AUTH_TYPES)[number];
export type McpConfigurationType = (typeof MCP_CONFIGURATION_TYPES)[number];

// ---------------------------------------------------------------------------------------------------- //
// INTERFACES
// ---------------------------------------------------------------------------------------------------- //

/**
 * Represents a tool associated with an MCP server.
 */
export interface McpTool {
  name: string;
  description: string;
  parameters: string;
}

/**
 * Represents a configuration entry for an MCP server.
 */
export interface McpConfiguration {
  type: McpConfigurationType;
  jsonFile: string;
}

/**
 * Single entry of `GET /mcp-servers`.
 */
export interface McpServerReduced {
  mcpServerId: string;
  mcpServerName: string;
  overview: string;
  agentService: string;
  status: string;
  isProvider: boolean;
  canEdit: boolean;
  canDeprecate: boolean;
  toBeDeprecated: boolean;
  /** Only populated when the caller has the `review_access` permission. */
  pendingAccessRequestsCount: number;
  infoClassification: string;
  businessDomain: string;
  serverType: McpServerType;
  serverDeploymentModel: McpServerDeploymentModel;
  hasCustomLogo: boolean;
  /** Presigned or static logo URL returned by the backend. */
  customLogoPresignedUrl: string;
  releaseDate: string;
  accessReviewStatus: AccessReviewStatus;
}

/**
 * Response of `GET /mcp-servers`. The next page is requested by passing the
 * returned cursor back as the `firstItemKey` query parameter.
 */
export interface McpServerListResponse {
  mcpServers: McpServerReduced[];
  lastItemKey?: string;
  isDevModeAuthorized?: boolean;
}

/**
 * Response of `GET /mcp-servers/{mcpServerId}`.
 */
export interface McpServerDetailed {
  mcpServerId: string;
  mcpServerName: string;
  overview: string;
  onboardingStatus: OnboardingStatus;
  status: string;
  isProvider: boolean;
  canEdit: boolean;
  canDeprecate: boolean;
  hasRequestedAccess: boolean;
  toBeDeprecated: boolean | null;
  deprecationDate: string | null;
  deprecationDurationDays: number | null;
  activeConsumersCount: number;
  l3Manager: ContactData;
  aiProductOwner: ContactData;
  informationOwner: ContactData;
  technicalContacts: ContactData[];
  infoClassification: string;
  businessDomain: string;
  countries: string[];
  accessRequestStatus: AccessRequestStatus | null;
  accessRequestId: string | null;
  cloudDeployments: CloudDeployment[];
  repoUrl: string;
  documentationUrl: string;
  acceptTerms: boolean;
  readmeMarkdown: string;
  serverType: McpServerType;
  providerNpmMailbox: string;
  serverDeploymentModel: McpServerDeploymentModel;
  configurations: McpConfiguration[];
  hasCustomLogo: boolean;
  customLogoPresignedUrl: string;

  leanixAppId?: string;
  tools?: McpTool[];
  serverUrl?: string;
  consumerServerUrl?: string;
  authType?: McpAuthType;
  tokenUrl?: string;
  oauthScope?: string;
  apiKeyLocation?: string;
  apiKeyLocationName?: string;
  apiKeyExpiresAt?: string;
  providerAccessReviewStatus?: AccessReviewStatus;
  accessReviewStatus?: AccessReviewStatus;
  nextAccessReviewAtDate?: string;
}

/** Response of `GET /mcp-servers/availability`. */
export interface McpServerNameAvailabilityResponse {
  available: boolean;
}

/**
 * Account types for the MCP Garden request access flow.
 */
export enum McpAccountType {
  TechnicalAccount = 'technicalAccount',
  EntraId = 'entraId',
}

export interface McpAccessRequestCreateRequest {
  accountType: McpAccountType;
  clientId?: string;
  applicationId?: string;
  businessNeed: string;
  countries: string[];
}

export interface McpAccessRequestCreateResponse {
  requestId: string;
  status: AccessRequestStatus;
  message: string;
}

/**
 * Access request record returned by `GET /mcp-servers/{mcpServerId}/access-requests`.
 */
export interface McpAccessRequest extends AccessRequestBase {
  status: AccessRequestStatus;
  businessDomain?: string;
  accessSyncedToDBX?: boolean;
}

/**
 * Response of `GET /mcp-servers/{mcpServerId}/access-requests`.
 */
export interface McpAccessRequestListResponse {
  requests: McpAccessRequest[];
}

/**
 * Body of `PUT /mcp-servers/{mcpServerId}/access-requests`.
 */
export interface McpAccessRequestBulkUpdateRequest {
  requestIds: string[];
  decision: AccessRequestStatus;
  decisionNotes?: string;
}

/**
 * Single result item returned in a bulk update response.
 */
export interface McpAccessRequestBulkUpdateResult extends AccessRequestBulkUpdateResultBase {}

/**
 * Response of `PUT /mcp-servers/{mcpServerId}/access-requests`.
 */
export interface McpAccessRequestBulkUpdateResponse extends AccessRequestBulkUpdateResponseBase {
  results: McpAccessRequestBulkUpdateResult[];
}

export interface McpAccessRequestUpdateRequest {
  decision: AccessRequestStatus;
  decisionNotes: string;
}

export interface McpAccessRequestUpdateResponse {
  requestId: string;
  status: AccessRequestStatus;
  decidedBy: string;
  decidedAtUTC: string;
  message: string;
}

/**
 * Body of `POST /mcp-servers`. Mirrors the fields collected by the
 * `mcp-server-onboarding-template` scaffolder template.
 */
export interface McpServerCreateRequest {
  mcpServerName: string;
  agentService: string;
  l3Manager: ContactData;
  aiProductOwner: ContactData;
  informationOwner: ContactData;
  technicalContacts: ContactData[];
  infoClassification: string;
  businessDomain: string;
  overview: string;
  acceptTerms: boolean;
  serverType: McpServerType;
  serverDeploymentModel: McpServerDeploymentModel;
  configurations?: McpConfiguration[];
  leanixAppId?: string;
  countries?: string[];
  repoUrl?: string;
  documentationUrl?: string;
  providerNpmMailbox?: string;
  readmeMarkdown?: string;
  tools?: McpTool[];
  endpointUrl?: string;
  authType?: McpAuthType;
  apiKeyLocation?: string;
  apiKeyLocationName?: string;
  apiKey?: string;
  apiKeyExpiresAt?: string;
  tokenUrl?: string;
  clientId?: string;
  clientSecret?: string;
  oauthScope?: string;
}

/** Response of `POST /mcp-servers`. */
export interface McpServerCreateResponse {
  mcpServerId: string;
  mcpServerName: string;
  onboardingStatus: OnboardingStatus;
  message: string;
}

/** Body of `PUT /mcp-servers/{mcpServerId}`. Only supplied fields are updated. */
export type McpServerUpdateRequest = Partial<
  Omit<McpServerCreateRequest, 'mcpServerName'>
> & {
  status?: string;
};

/** Response of `PUT /mcp-servers/{mcpServerId}`. */
export interface McpServerUpdateResponse {
  mcpServerId: string;
  mcpServerName: string;
  onboardingStatus: OnboardingStatus;
  message: string;
  updatedFields: string[];
}
